import { useEffect, useMemo, useState } from 'react';
import {
  GameMode,
  GameVariation,
  OnlineColorPreference,
  OnlineLobbyState,
  OnlineRoomRole,
  OnlineUser,
  PlayerPieces,
  PromotionRule,
  TimeControl
} from '../types';
import { formatTimeControlLabel } from '../utils/timeControlUtils';
import { formatChess960Position } from '../utils/chess960Utils';
import { Chess960PositionSelector } from './Chess960PositionSelector';
import { GameVariantSelector } from './GameVariantSelector';
import { OnlineLobbyPanel } from './OnlineLobbyPanel';
import { PieceCustomizer } from './PieceCustomizer';
import { TimeControlConfigModal } from './TimeControlConfigModal';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';

interface GameSetupWizardProps {
  gameVariation: GameVariation;
  setGameVariation: (variation: GameVariation) => void;
  chess960PositionId: number | null;
  setChess960PositionId: (id: number | null) => void;
  playerPieces: PlayerPieces;
  setPlayerPieces: React.Dispatch<React.SetStateAction<PlayerPieces>>;
  promotionRule: PromotionRule;
  setPromotionRule: (rule: PromotionRule) => void;
  gameMode: GameMode;
  setGameMode: (mode: GameMode) => void;
  onlineUser: OnlineUser | null;
  onlineRoomId: string | null;
  onlineRole: OnlineRoomRole | null;
  onlineRoomCode: string;
  setOnlineRoomCode: (roomCode: string) => void;
  onlineEntryMode: 'lobby' | 'private';
  setOnlineEntryMode: (mode: 'lobby' | 'private') => void;
  lobbyState: OnlineLobbyState;
  inviteColorPreference: OnlineColorPreference;
  setInviteColorPreference: (preference: OnlineColorPreference) => void;
  selectedLobbyUserId: string | null;
  setSelectedLobbyUserId: (userId: string | null) => void;
  onlineBusy: boolean;
  onlineStatus: string;
  onlineError: string | null;
  githubLoginUrl: string;
  googleLoginUrl: string;
  facebookLoginUrl: string;
  lichessLoginUrl: string;
  logoutUrl: string;
  joinOnlineRoom: () => void;
  leaveOnlineRoom: () => void;
  sendLobbyInvite: (chess960PositionId?: number | null) => void;
  acceptLobbyInvite: (inviteId: string) => void;
  refuseLobbyInvite: (inviteId: string) => void;
  cancelLobbyInvite: (inviteId: string) => void;
  startGame: (chess960PositionId?: number | null) => void;
  timeControl: TimeControl | null;
  setTimeControl: (tc: TimeControl | null) => void;
  backendBaseUrl: string;
  authHeaders: () => Record<string, string>;
}

type WizardStep = 'variant' | 'pieces' | 'online' | 'ready';
type OnlineSetupModal = 'variant' | 'pieces' | 'promotion' | 'color' | 'time' | 'chess960' | null;
type LocalSetupModal = 'variant' | 'pieces' | 'promotion' | 'time' | 'chess960' | null;
interface PostAuthResumeState {
  gameMode?: GameMode;
  step?: WizardStep;
  onlineEntryMode?: 'lobby' | 'private';
}

const POST_AUTH_RESUME_KEY = 'chess960x32_post_auth_resume';

const STEP_LABELS: Record<WizardStep, string> = {
  variant: 'Game Type',
  pieces: 'Pieces',
  online: 'Online Setup',
  ready: 'Ready'
};

const formatVariant = (variation: GameVariation): string => {
  switch (variation) {
    case 'chess':
      return 'Chess';
    case 'chessx2':
      return 'Chessx2';
    case 'chessx8':
      return 'Chessx8';
    case 'chessx32':
      return 'Chessx32';
    case 'chess960':
      return 'Chess960';
    case 'chess960x2':
      return 'Chess960x2';
    case 'chess960x8':
      return 'Chess960x8';
    case 'chess960x32':
      return 'Chess960x32';
    case 'chess960blind':
      return 'Blind Chess960';
  }
};

const formatPiece = (piece: PlayerPieces['white']): string => {
  switch (piece) {
    case 'Q':
      return 'Queen';
    case 'H':
      return 'Hawk';
    case 'E':
      return 'Elephant';
    case 'A':
      return 'Amazon';
    default:
      return '—';
  }
};

const formatPromotionRule = (rule: PromotionRule): string => rule === 'full' ? 'Full promotion' : 'Limited promotion';

const formatColorPreference = (preference: OnlineColorPreference): string => {
  switch (preference) {
    case 'white':
      return 'Play as White';
    case 'black':
      return 'Play as Black';
    default:
      return 'Random color';
  }
};





const readPostAuthResume = (): PostAuthResumeState | null => {
  try {
    const storedValue = window.localStorage.getItem(POST_AUTH_RESUME_KEY);
    if (!storedValue) {
      return null;
    }

    return JSON.parse(storedValue) as PostAuthResumeState;
  } catch {
    return null;
  }
};

export const GameSetupWizard = ({
  gameVariation,
  setGameVariation,
  chess960PositionId,
  setChess960PositionId,
  playerPieces,
  setPlayerPieces,
  promotionRule,
  setPromotionRule,
  gameMode,
  setGameMode,
  onlineUser,
  onlineRoomId,
  onlineRole,
  onlineRoomCode,
  setOnlineRoomCode,
  onlineEntryMode,
  setOnlineEntryMode,
  lobbyState,
  inviteColorPreference,
  setInviteColorPreference,
  selectedLobbyUserId,
  setSelectedLobbyUserId,
  onlineBusy,
  onlineStatus,
  onlineError,
  githubLoginUrl,
  googleLoginUrl,
  // facebookLoginUrl,
  lichessLoginUrl,
  logoutUrl,
  joinOnlineRoom,
  leaveOnlineRoom,
  sendLobbyInvite,
  acceptLobbyInvite,
  refuseLobbyInvite,
  cancelLobbyInvite,
  startGame,
  timeControl,
  setTimeControl,
  backendBaseUrl,
  authHeaders
}: GameSetupWizardProps) => {
  const [postAuthResume] = useState<PostAuthResumeState | null>(() => readPostAuthResume());
  const [requestedStep, setRequestedStep] = useState<WizardStep>(() => (
    postAuthResume?.step === 'online' ? 'online' : (gameMode === 'online' ? 'online' : 'variant')
  ));
  const [isPieceCustomizerExpanded, setIsPieceCustomizerExpanded] = useState(false);
  const [activeOnlineSetupModal, setActiveOnlineSetupModal] = useState<OnlineSetupModal>(null);
  const [activeLocalSetupModal, setActiveLocalSetupModal] = useState<LocalSetupModal>(null);
  const [deployEnvLoading, setDeployEnvLoading] = useState(false);
  const appBasePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  const privacyPolicyUrl = `${appBasePath}/privacy-policy.html`;
  const dataDeletionUrl = `${appBasePath}/data-deletion.html`;

  const renderLegalLinks = () => (
    <div className="rounded-2xl border border-cyan-400/8 bg-[#0f172a]/60 px-4 py-3 text-left text-xs text-slate-500">
      <div className="font-semibold uppercase tracking-[0.18em] text-slate-400">Review Links</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <a
          href={privacyPolicyUrl}
          target="_blank"
          rel="noreferrer"
          className="text-slate-200 underline decoration-slate-500/70 underline-offset-4 transition-colors hover:text-cyan-200"
        >
          Privacy Policy
        </a>
        <a
          href={dataDeletionUrl}
          target="_blank"
          rel="noreferrer"
          className="text-slate-200 underline decoration-slate-500/70 underline-offset-4 transition-colors hover:text-emerald-200"
        >
          User Data Deletion
        </a>
      </div>
    </div>
  );

  const fetchDeployEnvVariables = async () => {
    if (!onlineUser || !(
      (onlineUser.provider === 'github' && onlineUser.name === 'topce') ||
      (onlineUser.provider === 'lichess' && onlineUser.name === 'topce')
    )) {
      return;
    }

    setDeployEnvLoading(true);

    try {
      const response = await fetch(`${backendBaseUrl}/api/admin/deploy-env`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Show the data in an alert for now
      alert(`Server Environment Variables:\n\n${data.message}\nTotal Variables: ${data.totalCount}\nSensitive Variables: ${data.sensitiveCount}\n\nCheck console for full details.`);
      console.log('Server Environment Variables:', data);
      
    } catch (error) {
       alert(`Error fetching environment variables: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setDeployEnvLoading(false);
    }
  };

  // Default to 5 min / no increment when no time control is explicitly chosen (subtask 1.6)
  const effectiveTimeControl: TimeControl | null = timeControl ?? { minutes: 5, increment: 0, incrementType: 'none' };

  const storePostAuthResume = () => {
    try {
      window.localStorage.setItem(POST_AUTH_RESUME_KEY, JSON.stringify({
        gameMode: 'online',
        step: 'online',
        onlineEntryMode,
      }));
    } catch {
      // Ignore storage failures and continue with auth.
    }
  };

  const isStandardOr960 = ['chess', 'chessx2', 'chess960', 'chess960x2'].includes(gameVariation);
  const isOnlineBlackInRoom = gameMode === 'online' && onlineRoomId && onlineRole === 'black';

  const activeSteps = useMemo((): WizardStep[] => {
    if (gameMode === 'online') {
      return ['online'];
    }
    const steps: WizardStep[] = ['variant'];
    if (!isStandardOr960) {
      steps.push('pieces');
    }
    steps.push('ready');
    return steps;
  }, [gameMode, isStandardOr960]);

  useEffect(() => {
    if (!postAuthResume) {
      return;
    }

    try {
      window.localStorage.removeItem(POST_AUTH_RESUME_KEY);

      if (postAuthResume.gameMode === 'online') {
        setGameMode('online');
      }

      if (postAuthResume.onlineEntryMode === 'lobby' || postAuthResume.onlineEntryMode === 'private') {
        setOnlineEntryMode(postAuthResume.onlineEntryMode);
      }
    } catch {
      window.localStorage.removeItem(POST_AUTH_RESUME_KEY);
    }
  }, [postAuthResume, setGameMode, setOnlineEntryMode]);

  const visibleStep = activeSteps.includes(requestedStep)
    ? requestedStep
    : activeSteps[0];
  const currentStepIndex = Math.max(activeSteps.indexOf(visibleStep), 0);
  const startDisabled = (!isStandardOr960 && (!playerPieces.white || !playerPieces.black))
    || (gameMode === 'online' && !onlineUser)
    || onlineBusy;
  const canShowStartAction = !isOnlineBlackInRoom
    && !(gameMode === 'online' && onlineEntryMode === 'lobby');
  const onlinePiecesSummary = `${formatPiece(playerPieces.white)} / ${formatPiece(playerPieces.black)}`;
  const localPiecesSummary = `${formatPiece(playerPieces.white)} / ${formatPiece(playerPieces.black)}`;

  const renderOnlineSetupSummaryButton = (
    label: string,
    value: string,
    modal: Exclude<OnlineSetupModal, null>,
    description: string
  ) => (
    <button
      onClick={() => setActiveOnlineSetupModal(modal)}
      className="rounded-xl border border-white/5 bg-[#0f172a]/50 p-4 text-left transition-colors hover:bg-cyan-900/20"
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-2 text-base font-semibold text-white">{value}</div>
      <div className="mt-1 text-sm text-slate-400">{description}</div>
    </button>
  );

  const renderLocalSetupSummaryButton = (
    label: string,
    value: string,
    modal: Exclude<LocalSetupModal, null>,
    description: string
  ) => (
    <button
      onClick={() => setActiveLocalSetupModal(modal)}
      className="rounded-xl border border-white/5 bg-[#0f172a]/50 p-4 text-left transition-colors hover:bg-cyan-900/20"
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-2 text-base font-semibold text-white">{value}</div>
      <div className="mt-1 text-sm text-slate-400">{description}</div>
    </button>
  );

  const renderOnlineSetupModals = () => (
    <>
      <Modal
        isOpen={activeOnlineSetupModal === 'variant'}
        onClose={() => setActiveOnlineSetupModal(null)}
        title="Choose Variant"
        size="xl"
      >
        <div className="space-y-4">
          <GameVariantSelector
            gameVariation={gameVariation}
            setGameVariation={setGameVariation}
          gameMode={gameMode}
          />
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setActiveOnlineSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={activeOnlineSetupModal === 'pieces'}
        onClose={() => setActiveOnlineSetupModal(null)}
        title="Choose Pieces"
        size="xl"
      >
        <div className="space-y-4">
          <PieceCustomizer
            gameVariation={gameVariation}
            playerPieces={playerPieces}
            setPlayerPieces={setPlayerPieces}
            isExpanded={true}
          />
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setActiveOnlineSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={activeOnlineSetupModal === 'promotion'}
        onClose={() => setActiveOnlineSetupModal(null)}
        title="Promotion Rules"
        size="md"
      >
        <div className="space-y-3">
          <button
            onClick={() => setPromotionRule('limited')}
            aria-pressed={promotionRule === 'limited'}
            className={`w-full rounded-xl border p-4 text-left transition-colors ${
              promotionRule === 'limited'
                ? 'border-cyan-300/70 bg-cyan-500/15 text-white'
                : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
            }`}
          >
            <div className="font-semibold">Limited promotion</div>
            <p className="mt-1 text-sm text-slate-300">Traditional promotion to queen, rook, bishop, or knight.</p>
          </button>
          <button
            onClick={() => setPromotionRule('full')}
            aria-pressed={promotionRule === 'full'}
            className={`w-full rounded-xl border p-4 text-left transition-colors ${
              promotionRule === 'full'
                ? 'border-cyan-300/70 bg-cyan-500/15 text-white'
                : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
            }`}
          >
            <div className="font-semibold">Full promotion</div>
            <p className="mt-1 text-sm text-slate-300">Extended promotion adds hawk, elephant, and amazon where allowed.</p>
          </button>
          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={() => setActiveOnlineSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={activeOnlineSetupModal === 'color'}
        onClose={() => setActiveOnlineSetupModal(null)}
        title="Invite Color"
        size="md"
      >
        <div className="space-y-3">
          {(['white', 'black', 'random'] as OnlineColorPreference[]).map((preference) => (
            <button
              key={preference}
              onClick={() => setInviteColorPreference(preference)}
              className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${inviteColorPreference === preference
                ? 'border-cyan-300/70 bg-cyan-500/20 text-white'
                : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
              }`}
            >
              {formatColorPreference(preference)}
            </button>
          ))}
          <p className="text-sm text-slate-400">This affects lobby invites. Private rooms ignore color preference.</p>
          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={() => setActiveOnlineSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <TimeControlConfigModal
        isOpen={activeOnlineSetupModal === 'time'}
        onClose={() => setActiveOnlineSetupModal(null)}
        timeControl={timeControl}
        setTimeControl={setTimeControl}
        title="Time Control"
      />

      <Modal
        isOpen={activeOnlineSetupModal === 'chess960'}
        onClose={() => setActiveOnlineSetupModal(null)}
        title="Chess960 Position"
        size="lg"
      >
        <div className="space-y-4">
          <Chess960PositionSelector
            value={chess960PositionId}
            onChange={(id) => {
              setChess960PositionId(id);
              setActiveOnlineSetupModal(null);
            }}
          />
        </div>
      </Modal>
    </>
  );

  const renderLocalSetupModals = () => (
    <>
      <Modal
        isOpen={activeLocalSetupModal === 'variant'}
        onClose={() => setActiveLocalSetupModal(null)}
        title="Choose Variant"
        size="xl"
      >
        <div className="space-y-4">
          <GameVariantSelector
            gameVariation={gameVariation}
            setGameVariation={setGameVariation}
          gameMode={gameMode}
          />
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setActiveLocalSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={activeLocalSetupModal === 'pieces'}
        onClose={() => setActiveLocalSetupModal(null)}
        title="Choose Pieces"
        size="xl"
      >
        <div className="space-y-4">
          <PieceCustomizer
            gameVariation={gameVariation}
            playerPieces={playerPieces}
            setPlayerPieces={setPlayerPieces}
            isExpanded={isPieceCustomizerExpanded}
            onToggleExpand={() => setIsPieceCustomizerExpanded((expanded) => !expanded)}
          />
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setActiveLocalSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={activeLocalSetupModal === 'promotion'}
        onClose={() => setActiveLocalSetupModal(null)}
        title="Promotion Rule"
        size="md"
      >
        <div className="space-y-3">
          <button
            onClick={() => setPromotionRule('limited')}
            aria-pressed={promotionRule === 'limited'}
            className={`w-full rounded-xl border p-4 text-left transition-colors ${
              promotionRule === 'limited'
                ? 'border-cyan-300/70 bg-cyan-500/15 text-white'
                : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
            }`}
          >
            <div className="font-semibold">Limited promotion</div>
            <p className="mt-1 text-sm text-slate-300">Traditional promotion to queen, rook, bishop, or knight.</p>
          </button>
          <button
            onClick={() => setPromotionRule('full')}
            aria-pressed={promotionRule === 'full'}
            className={`w-full rounded-xl border p-4 text-left transition-colors ${
              promotionRule === 'full'
                ? 'border-cyan-300/70 bg-cyan-500/15 text-white'
                : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
            }`}
          >
            <div className="font-semibold">Full promotion</div>
            <p className="mt-1 text-sm text-slate-300">Extended promotion adds hawk, elephant, and amazon where allowed.</p>
          </button>
          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={() => setActiveLocalSetupModal(null)}>Done</Button>
          </div>
        </div>
      </Modal>

      <TimeControlConfigModal
        isOpen={activeLocalSetupModal === 'time'}
        onClose={() => setActiveLocalSetupModal(null)}
        timeControl={timeControl}
        setTimeControl={setTimeControl}
        title="Time Control"
      />

      <Modal
        isOpen={activeLocalSetupModal === 'chess960'}
        onClose={() => setActiveLocalSetupModal(null)}
        title="Chess960 Position"
        size="lg"
      >
        <div className="space-y-4">
          <Chess960PositionSelector
            value={chess960PositionId}
            onChange={(id) => {
              setChess960PositionId(id);
              setActiveLocalSetupModal(null);
            }}
          />
        </div>
      </Modal>
    </>
  );

  const handleNextStep = () => {
    if (currentStepIndex < activeSteps.length - 1) {
      setRequestedStep(activeSteps[currentStepIndex + 1]);
    }
  };

  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setRequestedStep(activeSteps[currentStepIndex - 1]);
    }
  };

  const renderOnlineControls = () => {
    if (!onlineUser) {
      return (
        <Card variant="glass" padding="lg">
          <div className="space-y-4 text-center">
            <div>
              <p className="text-slate-300">You need to sign in to play online games.</p>
              {onlineRoomCode && (
                <p className="mt-3 rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3 text-sm text-cyan-100">
                  You&apos;ve been invited to room <span className="font-bold">{onlineRoomCode}</span>. Sign in to join it.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <a
                href={githubLoginUrl}
                onClick={storePostAuthResume}
                className="inline-flex items-center justify-center rounded-lg border border-white/5 bg-[#0f172a]/70 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cyan-900/30"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
                </svg>
                Continue with GitHub
              </a>
              <a
                href={googleLoginUrl}
                onClick={storePostAuthResume}
                className="inline-flex items-center justify-center rounded-lg border border-white/5 bg-[#0f172a]/70 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cyan-900/30"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12.545 10.239v3.821h5.445c-.715 2.076-2.637 3.6-5.445 3.6-3.334 0-6.038-2.696-6.038-6.038s2.704-6.038 6.038-6.038c1.604 0 3.084.599 4.21 1.582l3.115-3.115c-1.937-1.806-4.488-2.913-7.325-2.913-6.038 0-10.932 4.894-10.932 10.932s4.894 10.932 10.932 10.932c9.369 0 10.932-8.317 10.932-10.932 0-.777-.068-1.366-.151-1.956h-10.781z" />
                </svg>
                Continue with Google
              </a>
              {/* <a
                href={facebookLoginUrl}
                onClick={storePostAuthResume}
                className="inline-flex items-center justify-center rounded-lg border border-cyan-500/50 bg-cyan-950/50 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cyan-900/70"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M13.5 22v-8.2h2.75l.41-3.2H13.5V8.56c0-.93.26-1.56 1.59-1.56h1.7V4.14c-.83-.09-1.67-.13-2.51-.12-2.48 0-4.19 1.52-4.19 4.31v2.27H7.25v3.2h2.84V22h3.41Z" />
                </svg>
                Continue with Facebook
              </a> */}
              <a
                href={lichessLoginUrl}
                onClick={storePostAuthResume}
                className="inline-flex items-center justify-center rounded-lg border border-white/5 bg-[#0f172a]/70 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-cyan-900/30"
              >
                <span
                  className="mr-2"
                  style={{
                    fontFamily: 'Noto Sans Symbols 2, Segoe UI Symbol, Apple Symbols, sans-serif',
                    fontWeight: 400,
                    lineHeight: 1,
                    fontSize: '1rem',
                    verticalAlign: 'middle'
                  }}
                  aria-hidden="true"
                >
                  ♘
                </span>
                Continue with Lichess
              </a>
            </div>
            {renderLegalLinks()}
          </div>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <Card variant="glass" padding="lg">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold text-white">Game Setup</h3>
              <p className="mt-1 text-sm text-slate-300">Click any setting to edit it in a popup without expanding the page.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {renderOnlineSetupSummaryButton('Variant', formatVariant(gameVariation), 'variant', 'Choose the online game type')}
              {renderOnlineSetupSummaryButton('Pieces', onlinePiecesSummary, 'pieces', 'Set major pieces for both sides')}
              {renderOnlineSetupSummaryButton('Promotion', formatPromotionRule(promotionRule), 'promotion', 'Switch promotion rules')}
              {renderOnlineSetupSummaryButton('Color', formatColorPreference(inviteColorPreference), 'color', 'Set lobby invite color preference')}
              {renderOnlineSetupSummaryButton('Time Control', formatTimeControlLabel(effectiveTimeControl), 'time', 'Set the clock for each player')}
              {gameVariation.includes('960') && gameVariation !== 'chess960blind' && renderOnlineSetupSummaryButton('Chess960 Position', formatChess960Position(chess960PositionId), 'chess960', 'Select a specific Chess960 starting position')}
            </div>
          </div>
        </Card>

        <Card variant="glass" padding="lg">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-300">Signed in as</p>
              <p className="text-lg font-semibold text-white">{onlineUser.name}</p>
            </div>
             <div className="flex flex-wrap gap-2">
               {/* Admin button for GitHub user "topce" or Lichess user "topce" */}
               {onlineUser && (
                 (onlineUser.provider === 'github' && onlineUser.name === 'topce') ||
                 (onlineUser.provider === 'lichess' && onlineUser.name === 'topce')
               ) && (
                 <button
                  onClick={() => fetchDeployEnvVariables()}
                  disabled={deployEnvLoading}
                  className="inline-flex items-center justify-center rounded-lg border border-cyan-600 bg-cyan-900/30 px-3 py-2 text-sm font-medium text-cyan-200 transition-colors hover:bg-cyan-800/40 disabled:opacity-50 disabled:cursor-not-allowed"
                   title="Show server environment variables"
                >
                  {deployEnvLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                       Show Env
                    </>
                  )}
                </button>
              )}
              <a
                href={logoutUrl}
                className="inline-flex items-center justify-center rounded-lg border border-cyan-400/10 bg-[#0f172a]/80 px-3 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-cyan-900/25"
              >
                Sign out
              </a>
            </div>
          </div>
          <div className="mt-4">
            {renderLegalLinks()}
          </div>
        </Card>

        {onlineRoomId ? (
          <Card variant="glass" padding="lg">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-300">Connected to room</p>
                <p className="font-mono text-xl font-semibold text-cyan-300">{onlineRoomId}</p>
                {onlineRole && (
                  <p className="mt-1 text-sm text-emerald-200">Playing as {onlineRole}</p>
                )}
              </div>
              {isOnlineBlackInRoom && (
                <p className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3 text-sm text-cyan-100">
                  Waiting for the host to start the game.
                </p>
              )}
              <Button variant="secondary" onClick={leaveOnlineRoom}>
                Leave Room
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <Card variant="glass" padding="lg">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setOnlineEntryMode('lobby')}
                    aria-pressed={onlineEntryMode === 'lobby'}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      onlineEntryMode === 'lobby'
                        ? 'border-cyan-300/70 bg-cyan-500/20 text-white'
                        : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
                    }`}
                  >
                    Lobby
                  </button>
                  <button
                    onClick={() => setOnlineEntryMode('private')}
                    aria-pressed={onlineEntryMode === 'private'}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      onlineEntryMode === 'private'
                        ? 'border-cyan-300/70 bg-cyan-500/20 text-white'
                        : 'border-white/5 bg-[#0f172a]/40 text-slate-300 hover:bg-cyan-900/20'
                    }`}
                  >
                    Private room
                  </button>
                </div>

                {onlineEntryMode === 'lobby' ? (
                  <OnlineLobbyPanel
                    onlineUser={onlineUser}
                    lobbyState={lobbyState}
                    selectedLobbyUserId={selectedLobbyUserId}
                    setSelectedLobbyUserId={setSelectedLobbyUserId}
                    onlineBusy={onlineBusy}
                    onSendInvite={() => sendLobbyInvite(chess960PositionId)}
                    onAcceptInvite={acceptLobbyInvite}
                    onRefuseInvite={refuseLobbyInvite}
                    onCancelInvite={cancelLobbyInvite}
                  />
                ) : onlineRoomCode ? (
                  <div className="space-y-3">
                    <p className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3 text-sm text-cyan-100">
                      {onlineBusy ? 'Joining' : 'Ready to join'} room <span className="font-bold">{onlineRoomCode}</span>.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={joinOnlineRoom} disabled={onlineBusy}>
                        {onlineBusy ? 'Joining...' : 'Join Game'}
                      </Button>
                      <Button variant="secondary" onClick={() => setOnlineRoomCode('')}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2 text-sm text-slate-300">
                      <p>1. Choose how you want to play online.</p>
                      <p>2. Adjust the game settings from the summary buttons above.</p>
                      <p>3. Start a private room or join an existing one.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-cyan-400/10" />
                      <span className="text-xs uppercase tracking-wider text-slate-500">or join an existing room</span>
                      <div className="h-px flex-1 bg-cyan-400/10" />
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Input
                        value={onlineRoomCode}
                        onChange={(event) => setOnlineRoomCode(event.target.value.trim().toUpperCase())}
                        placeholder="Enter room code"
                        aria-label="Room code"
                      />
                      <Button onClick={joinOnlineRoom} disabled={onlineBusy || !onlineRoomCode.trim()}>
                        Join
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}

        {(onlineStatus || onlineError) && (
          <Card variant="glass" padding="md">
            <div className="space-y-2 text-sm">
              {onlineStatus && <div className="text-slate-300">{onlineStatus}</div>}
              {onlineError && (
                <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-3 text-rose-200">
                  {onlineError}
                </div>
              )}
            </div>
          </Card>
        )}

        {renderOnlineSetupModals()}
      </div>
    );
  };

  const renderOnlineActions = () => {
    if (isOnlineBlackInRoom) {
      return (
        <Card variant="glass" padding="md">
          <p className="text-center text-sm text-slate-300">Waiting for the host to start the game...</p>
        </Card>
      );
    }

    if (onlineEntryMode === 'lobby') {
      return null;
    }

    return (
      <div className="space-y-2">
        <Button
          onClick={() => startGame(chess960PositionId)}
          disabled={startDisabled}
          size="lg"
          className={`w-full ${startDisabled ? 'opacity-50' : ''}`}
        >
          {onlineRoomId ? 'Start Game' : 'Start Private Room'}
        </Button>
        {!onlineRoomId && !onlineRoomCode && (
          <p className="text-center text-sm text-slate-400">
            Creates a private room with the current settings and opens the game.
          </p>
        )}
      </div>
    );
  };

  const renderReadyActions = () => {
    if (isOnlineBlackInRoom) {
      return (
        <Card variant="glass" padding="md">
          <p className="text-center text-sm text-slate-300">Waiting for the host to start the game...</p>
        </Card>
      );
    }

    if (!canShowStartAction) {
      return (
        <div className="space-y-2 text-center text-sm">
          {gameMode === 'online' && !onlineUser && (
            <p className="text-slate-300">Sign in on the Online step to start an online game.</p>
          )}
          {gameMode === 'online' && onlineEntryMode === 'lobby' && (
            <p className="text-slate-300">Use the Online step to send or accept a lobby invite before starting.</p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Button
          onClick={() => startGame(chess960PositionId)}
          disabled={startDisabled}
          size="lg"
          className={`w-full ${startDisabled ? 'opacity-50' : ''}`}
        >
          {gameMode === 'online' && !onlineRoomId ? 'Start Private Room' : 'Start Game'}
        </Button>
        {gameMode === 'online' && onlineEntryMode === 'private' && !onlineRoomId && !onlineRoomCode && (
          <p className="text-center text-sm text-slate-400">
            Creates a private room and opens the game. You&apos;ll get an invite link to share.
          </p>
        )}
      </div>
    );
  };

  const renderStep = () => {
    switch (visibleStep) {
      case 'variant':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">Choose Game Type</h2>
              <p className="mt-2 text-slate-300">Select your preferred chess variant.</p>
            </div>
            
            <GameVariantSelector
              gameVariation={gameVariation}
              setGameVariation={setGameVariation}
            gameMode={gameMode}
            />

            {/* Configuration grid for local games */}
            {gameMode === 'local' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-white">Game Settings</h3>
                  <p className="mt-1 text-sm text-slate-300">Click any setting to edit it in a popup.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {renderLocalSetupSummaryButton('Variant', formatVariant(gameVariation), 'variant', 'Choose game type')}
                  {!isStandardOr960 && renderLocalSetupSummaryButton('Pieces', localPiecesSummary, 'pieces', 'Set major pieces')}
                  {renderLocalSetupSummaryButton('Promotion', formatPromotionRule(promotionRule), 'promotion', 'Switch promotion rules')}
                  {renderLocalSetupSummaryButton('Time Control', formatTimeControlLabel(effectiveTimeControl), 'time', 'Set the clock for each player')}
                  {gameVariation.includes('960') && gameVariation !== 'chess960blind' && renderLocalSetupSummaryButton('Chess960 Position', formatChess960Position(chess960PositionId), 'chess960', 'Select a specific Chess960 starting position')}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setGameMode('online')}>Back to Online</Button>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => startGame(chess960PositionId)}>Start Game</Button>
                <Button onClick={handleNextStep}>
                  {isStandardOr960 ? 'Next: Review' : 'Next: Customize Pieces'}
                </Button>
              </div>
            </div>
          </div>
        );

      case 'pieces':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">Piece Customization</h2>
              <p className="mt-2 text-slate-300">Customize pieces for advanced variants.</p>
            </div>
            
            <PieceCustomizer
              gameVariation={gameVariation}
              playerPieces={playerPieces}
              setPlayerPieces={setPlayerPieces}
              isExpanded={isPieceCustomizerExpanded}
              onToggleExpand={() => setIsPieceCustomizerExpanded((expanded) => !expanded)}
            />

            {/* Configuration grid for local games */}
            {gameMode === 'local' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-white">Game Settings</h3>
                  <p className="mt-1 text-sm text-slate-300">Click any setting to edit it in a popup.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {renderLocalSetupSummaryButton('Variant', formatVariant(gameVariation), 'variant', 'Choose game type')}
                  {renderLocalSetupSummaryButton('Pieces', localPiecesSummary, 'pieces', 'Set major pieces')}
                  {renderLocalSetupSummaryButton('Promotion', formatPromotionRule(promotionRule), 'promotion', 'Switch promotion rules')}
                  {renderLocalSetupSummaryButton('Time Control', formatTimeControlLabel(effectiveTimeControl), 'time', 'Set the clock for each player')}
                  {gameVariation.includes('960') && gameVariation !== 'chess960blind' && renderLocalSetupSummaryButton('Chess960 Position', formatChess960Position(chess960PositionId), 'chess960', 'Select a specific Chess960 starting position')}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={handlePrevStep}>Back</Button>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => startGame(chess960PositionId)}>Start Game</Button>
                <Button onClick={handleNextStep}>Next: Review</Button>
              </div>
            </div>
          </div>
        );

      case 'online':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">Online Game Setup</h2>
              <p className="mt-2 text-slate-300">Set the rules here, then create a private room or send a lobby invite with those settings.</p>
            </div>
            {renderOnlineControls()}
            {renderOnlineActions() && (
              <Card variant="glass" padding="lg">
                {renderOnlineActions()}
              </Card>
            )}
            <div className="flex justify-center mt-8">
              <Button variant="ghost" onClick={() => setGameMode('local')}>
                Play Over the Board (Local)
              </Button>
            </div>
          </div>
        );

      case 'ready':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">Ready to Play</h2>
              <p className="mt-2 text-slate-300">Review your setup and launch the game.</p>
            </div>

            <Card variant="glass" padding="lg">
              <h3 className="mb-4 text-lg font-bold text-white">Game Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">Game Mode</span>
                  <Badge variant={gameMode === 'online' ? 'primary' : 'default'}>
                    {gameMode === 'online' ? 'Play Online' : 'Over the Board'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">Game Variant</span>
                  <Badge variant="outline">{gameVariation}</Badge>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">White Piece</span>
                  <Badge variant="outline">{playerPieces.white || 'Queen'}</Badge>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">Black Piece</span>
                  <Badge variant="outline">{playerPieces.black || 'Queen'}</Badge>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">Promotion Rule</span>
                  <Badge variant="outline">{promotionRule}</Badge>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-300">Time Control</span>
                  {gameMode === 'local' ? (
                    <button
                      onClick={() => setActiveLocalSetupModal('time')}
                      className="rounded-md border border-cyan-400/10 bg-[#0f172a]/50 px-2.5 py-0.5 text-xs font-medium text-slate-200 transition-colors hover:bg-cyan-900/25"
                    >
                      {formatTimeControlLabel(effectiveTimeControl)}
                    </button>
                  ) : (
                    <Badge variant="outline">{formatTimeControlLabel(effectiveTimeControl)}</Badge>
                  )}
                </div>
                {gameMode === 'online' && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-300">Online Entry</span>
                    <Badge variant="outline">{onlineEntryMode === 'lobby' ? 'Lobby invite' : 'Private room'}</Badge>
                  </div>
                )}
              </div>
            </Card>

            {gameMode === 'online' && onlineRoomId && (
              <Card variant="glass" padding="md">
                <div className="text-center">
                  <p className="text-slate-300">Current room</p>
                  <p className="font-mono text-lg font-semibold text-cyan-300">{onlineRoomId}</p>
                </div>
              </Card>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={handlePrevStep}>Back</Button>
              <div className="flex-1 pl-4">{renderReadyActions()}</div>
            </div>
          </div>
        );
    }
  };

  const progressWidth = activeSteps.length === 1
    ? 100
    : (currentStepIndex / (activeSteps.length - 1)) * 100;

  return (
    <>
      <div className="mb-8 rounded-2xl border border-cyan-400/10 bg-[#1e293b]/90 p-6 shadow-2xl backdrop-blur-xl">
        {activeSteps.length > 1 && (
          <div className="mb-8">
            <div className="mb-2 flex items-center justify-between gap-2">
              {activeSteps.map((step, index) => (
                <div key={step} className="flex-1 text-center">
                  <div
                    className={`mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full ${
                      index <= currentStepIndex
                        ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white'
                        : 'bg-[#0f172a]/80 text-slate-400'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span className={`text-xs ${step === visibleStep ? 'font-semibold text-white' : 'text-slate-400'}`}>
                    {STEP_LABELS[step]}
                  </span>
                </div>
              ))}
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-[#0f172a]/80">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 transition-all duration-300"
                style={{ width: `${progressWidth}%` }}
              />
            </div>
          </div>
        )}

        {renderStep()}
      </div>

      {/* Render modals */}
      {renderOnlineSetupModals()}
      {renderLocalSetupModals()}
    </>
  );
};
